import { Link } from "@tanstack/react-router";
import type * as React from "react";
import { useUsageQuery } from "@/components/billing/usage-widget";
import { NavHistory } from "@/components/nav-history";
import { NavMain } from "@/components/nav-main";
import { NavSecondary } from "@/components/nav-secondary";
import { NavTeammates } from "@/components/nav-teammates";
import { NavUser } from "@/components/nav-user";
import { ChecklistWidget } from "@/components/onboarding/checklist-widget";
import { Button } from "@/components/ui/button";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Spinner } from "@/components/ui/spinner";
import { PLAN_DISPLAY, UPGRADE_MAP, useUpgradeCheckout } from "@/components/upgrade-modal";
import { NAV_MAIN_ITEMS, NAV_SECONDARY_ITEMS } from "@/constants/navigation";
import { useUser } from "@/lib/auth/hooks";
import { ErrorBoundary } from "@/lib/errors/boundary";
import { getUserDisplayData } from "@/lib/utils/user-display";
import logoPng from "@/logo.png";
import { useOnboardingStore } from "@/stores/onboarding-store";

/** Compact billing limit card — mirrors the upgrade modal's inner card. Only renders when at limit. */
function SidebarLimitBanner() {
	const { data: usage } = useUsageQuery();
	const { checkout, isLoading } = useUpgradeCheckout();
	if (!usage || (usage.runs_used < usage.runs_limit && !usage.cost_limit_hit)) return null;

	const upgradeTarget = UPGRADE_MAP[usage.plan];
	const planName = PLAN_DISPLAY[usage.plan] ?? usage.plan;
	const isCostLimitOnly = usage.cost_limit_hit && usage.runs_used < usage.runs_limit;

	return (
		<div className="px-2 pb-2">
			<div className="rounded-lg border bg-card p-3 space-y-2">
				<p className="text-xs text-muted-foreground">
					{isCostLimitOnly
						? `Fair usage limit reached \u00B7 ${planName}`
						: `${usage.runs_used}/${usage.runs_limit} runs used \u00B7 ${planName}`}
				</p>
				{upgradeTarget ? (
					<>
						<div className="flex items-center justify-between text-xs">
							<span className="font-medium">{upgradeTarget.name}</span>
							<span className="text-muted-foreground">${upgradeTarget.price}/mo</span>
						</div>
						<Button
							size="sm"
							className="h-7 w-full text-xs"
							disabled={isLoading}
							onClick={() => checkout(upgradeTarget.planId)}
						>
							{isLoading ? <Spinner className="size-4" /> : `Upgrade to ${upgradeTarget.name}`}
						</Button>
					</>
				) : (
					<Button size="sm" variant="outline" className="h-7 w-full text-xs" asChild>
						<Link to="/billing">View plans</Link>
					</Button>
				)}
			</div>
		</div>
	);
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
	const user = useUser();
	const { openOnboarding } = useOnboardingStore();

	// Build user data for NavUser
	const userData = getUserDisplayData(user);

	// Handle onboarding button click
	const handleOnboardingClick = (e: React.MouseEvent) => {
		e.preventDefault();
		openOnboarding();
	};

	// Update nav items to handle onboarding click (if onboarding is in nav)
	const navSecondaryWithHandlers = NAV_SECONDARY_ITEMS.map((item) =>
		item.title === "Onboarding" ? { ...item, onClick: handleOnboardingClick } : item
	);

	return (
		<Sidebar collapsible="offcanvas" {...props}>
			<SidebarHeader>
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton asChild className="data-[slot=sidebar-menu-button]:!p-1.5">
							<Link to="/">
								<img src={logoPng} alt="m8tes.ai" className="h-22 w-auto shrink-0 dark:invert" />
							</Link>
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarHeader>
			<SidebarContent>
				{/* Main navigation */}
				<NavMain items={NAV_MAIN_ITEMS} />

				{/* Teammates section */}
				<NavTeammates />

				{/* History section */}
				<NavHistory />

				{/* Spacer to push content to bottom */}
				<div className="flex-1" />

				{/* Billing limit CTA — only when at limit */}
				<ErrorBoundary fallback={null}>
					<SidebarLimitBanner />
				</ErrorBoundary>

				{/* Checklist widget - above settings (isolated so crashes don't kill the page) */}
				<ErrorBoundary fallback={null}>
					<div className="px-2 pb-2">
						<ChecklistWidget />
					</div>
				</ErrorBoundary>

				{/* Secondary navigation */}
				<NavSecondary items={navSecondaryWithHandlers} />
			</SidebarContent>
			<SidebarFooter>
				<NavUser user={userData} />
			</SidebarFooter>
		</Sidebar>
	);
}
